// src/rms/pages/CheckoutFlow.js
// ============================================================================
// Checkout
// ============================================================================
// A transactional screen: NO ad slots appear anywhere in this file, and the
// corresponding slot keys are registered with adsAllowed:false so none can be
// introduced by accident.
//
// The steps themselves are assembled from the vertical's workflow rules — a
// boat rental gets a waiver step, a tool rental doesn't, construction gets a
// certification gate — without this component knowing which vertical it is.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import db from "../data";
import rentalService from "../data/rentalService";
import { useRms } from "../context/RmsContext";
import {
  PageHeader, Banner, Field, TextInput, DynamicField, SignaturePad, StatusPill, formatDateTime,
} from "../components/ui";
import { fieldsCapturedAt, optionsForField } from "../verticals/presets";
import { optionLabel } from "../i18n/optionLabel";
import adEngine from "../ads/adEngine";
import PinModal from "../components/PinModal";
import pinService from "../data/pinService";
import agreementDocumentService from "../data/agreementDocumentService";
import { uploadSignature } from "../data/documentStorage";
import AgreementReader from "../components/AgreementReader";

function CheckoutFlow() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { operator, config, t, tLower, formatMoney, workflow, invalidate, isContinuous, member } = useRms();

  const [reservation, setReservation] = useState(null);
  const [asset, setAsset] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [captured, setCaptured] = useState({});
  const [waiverAccepted, setWaiverAccepted] = useState(false);

  // The operator's own agreement text, rendered for THIS booking. Empty when
  // the owner has never opened Settings > Agreements, in which case the old
  // placeholder waiver still shows -- this is additive and must not stop a
  // counter that worked yesterday.
  const [documents, setDocuments] = useState([]);

  // Distinct from the signature, and stored in its own column, because ESIGN
  // and UETA treat agreeing to transact electronically as a separate act from
  // signing. Two ticks at the counter, two timestamps, seconds apart.
  const [consentAt, setConsentAt] = useState(null);
  const [signerName, setSignerName] = useState("");

  // Set once the documents are frozen. Kept so that a checkout which fails
  // AFTER the customer signed can be retried without signing them twice.
  const [signedDocs, setSignedDocs] = useState(null);
  const [signaturePath, setSignaturePath] = useState(null);
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);

  // See CheckinFlow: the refusal must not render where nobody is looking.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = (await db.get("reservations", reservationId)).data;
      if (!r || cancelled) {
        setLoading(false);
        return;
      }
      const a = (await db.get("assets", r.asset_id)).data;
      const c = (await db.get("customers", r.customer_id)).data;
      const b = await rentalService.checkoutBlockers(operator, r, a, c);
      if (cancelled) return;

      // The location is only needed for {{pickup_location}}; a booking without
      // one still renders, with that token resolving to an empty string.
      const loc = r.location_id ? (await db.get("locations", r.location_id)).data : null;

      // rate_snapshot is the quote taken at booking time. Check-in bills from
      // it rather than re-pricing, so the contract quotes the same figure -- a
      // rate change after the fact must not rewrite what was agreed.
      const rendered = await agreementDocumentService.renderForBooking(operator, {
        customer: c,
        asset: a,
        reservation: r,
        location: loc,
        quote: r.rate_snapshot,
        formatMoney,
      });
      if (cancelled) return;

      setReservation(r);
      setAsset(a);
      setCustomer(c);
      setBlockers(b);
      setDocuments(rendered.success ? rendered.data : []);
      setSignerName([c?.first_name, c?.last_name].filter(Boolean).join(" "));
      // Pre-fill the meters with the asset's last known values so staff adjust
      // rather than retype.
      setCaptured({ ...(a?.custom_fields || {}) });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reservationId, operator, formatMoney]);

  // Shown, not enforced -- blocking a counter over something only the owner can
  // fix would make the wrong party pay for it.
  const blanksInDocuments = useMemo(
    () => [...new Set(documents.flatMap((d) => d.blanks))],
    [documents]
  );
  const unknownInDocuments = useMemo(
    () => [...new Set(documents.flatMap((d) => d.unknown))],
    [documents]
  );

  const checkoutFields = useMemo(
    () => (config ? fieldsCapturedAt(config.key, "checkout", operator?.config_overrides) : []),
    [config, operator]
  );

  const steps = useMemo(() => {
    const s = [{ key: "verify", label: t("checkout.step.verify", "Verify") }];
    if (workflow.certificationRequired) s.push({ key: "cert", label: t("certifications.certification", "Certification") });
    if (checkoutFields.length) s.push({ key: "condition", label: t("checkout.step.condition", "Condition") });
    if (workflow.waiverRequired) s.push({ key: "waiver", label: t("checkout.step.waiver", "Waiver") });
    s.push({ key: "sign", label: t("checkout.step.sign", "Sign & release") });
    return s;
  }, [workflow, checkoutFields, t]);

  if (loading) return <p style={{ color: "var(--rms-muted-strong)" }}>Loading…</p>;
  if (!reservation) {
    return (
      <>
        <PageHeader title={t("checkoutFlow.notFound", "Not found")} subtitle={t("checkoutFlow.thatReservationNoLonger", "That reservation no longer exists.")} />
        <Link className="rms-btn rms-btn--ghost" to="/rms/reservations">
          {t("checkout.backTo", "Back to {bookings}", {
            bookings: tLower("reservationPlural", "reservations"),
          })}
        </Link>
      </>
    );
  }

  const usingDocuments = documents.length > 0;

  // With real documents the acknowledgement IS the electronic-records consent,
  // and it needs a name against it. Without them, the old checkbox stands.
  const contractAccepted = usingDocuments
    ? !!consentAt && !!signerName.trim()
    : !workflow.waiverRequired || waiverAccepted;

  const canRelease =
    blockers.length === 0 &&
    contractAccepted &&
    (!workflow.signatureRequiredAtCheckout || !!signature) &&
    requiredFieldsPresent(checkoutFields, captured);

  /**
   * Handing the asset over is signed.
   *
   * The PIN is collected FIRST and its signature id travels with the write, so
   * the two cannot come apart: a trigger on rms_agreements refuses an insert
   * whose signature was not minted moments ago for this operator. Signing
   * afterwards would leave a window in which the vehicle is out and the log
   * says nobody released it.
   */
  const handleRelease = async () => {
    // Same reasoning as check-in: validate before minting a signature, so a
    // refusal cannot leave the append-only log claiming the vehicle went out.
    const pre = await rentalService.validateCheckOut(operator, {
      reservationId,
      signaturePath: signature,
      waiverAccepted: contractAccepted,
    });
    if (!pre.success) {
      setError(pre.error);
      return;
    }
    setError(null);
    setSigning(true);
  };

  const release = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "agreement.open",
      subjectTable: "rms_reservations",
      subjectId: reservationId,
    });
    if (!signed.success) return { error: signed.error };

    setBusy(true);
    adEngine.recordAction();

    // 1. File the signature. It used to go into checkout_signature_path as a
    //    base64 data: URL -- a PNG in a text column named `_path`, which nothing
    //    ever read back. Now it goes to the private bucket and the column holds
    //    what it says it holds.
    let path = signaturePath;
    if (signature && !path) {
      const filed = await uploadSignature(signature, {
        operatorId: operator.id,
        customerId: customer.id,
      });
      if (!filed.success) {
        setBusy(false);
        return { error: filed.error };
      }
      path = filed.data.storage_path;
      setSignaturePath(path);
    }

    // 2. Freeze the contract, in the words on screen a moment ago.
    //    Skipped when a previous attempt already did it: a checkout that failed
    //    at step 3 must be retryable without asking the customer to sign twice
    //    and without leaving two signed copies behind.
    let docs = signedDocs;
    if (usingDocuments && !docs) {
      const frozen = await agreementDocumentService.signAtCounter(operator, documents, {
        customerId: customer.id,
        reservationId,
        signerName,
        signerEmail: customer.email,
        signaturePath: path,
        consentedAt: consentAt,
        memberId: signed.member?.id || member?.id,
      });
      if (!frozen.success) {
        setBusy(false);
        return { error: frozen.error };
      }
      docs = frozen.data;
      setSignedDocs(docs);
    }

    // 3. Hand it over.
    const result = await rentalService.checkOut(operator, {
      reservationId,
      capturedFields: captured,
      signaturePath: path || signature,
      waiverAccepted: contractAccepted,
      notes,
      signatureId: signed.data,
      // Was the string literal "v1" for as long as this table has existed. Now
      // it names the row holding the exact text that was agreed to.
      waiverVersion: docs?.[0]?.id || null,
    });
    setBusy(false);
    setSigning(false);

    if (!result.success) {
      setError(result.error);
      return {};
    }

    // 4. Bind the documents to the agreement now that it exists. The trigger
    //    lets this one column arrive late on a signed row precisely for this.
    await agreementDocumentService.attachAgreement(docs, result.data?.id);

    invalidate();
    navigate("/rms");
    return {};
  };

  return (
    <>
    <>
      <PageHeader
        title={t("checkout.title", "{action} — {item}", {
          action: t("checkout", "Check out"),
          item: asset?.name,
        })}
        subtitle={`${reservation.reservation_number} · ${customer?.first_name} ${customer?.last_name || ""}`}
        actions={
          <Link className="rms-btn rms-btn--ghost" to="/rms/reservations">
            {t("action.cancel", "Cancel")}
          </Link>
        }
      />

      <div className="rms-steps">
        {steps.map((s, i) => (
          <span key={s.key} className="rms-step">
            {i + 1}. {s.label}
          </span>
        ))}
      </div>

      {error ? (
        <div ref={errorRef}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <Banner tone="danger">
          <div>
            <strong>
              {t("checkoutFlow.cannotRelease", "Cannot release this {item} yet.", {
                item: tLower("assetSingular", "asset"),
              })}
            </strong>
            <ul style={{ margin: "6px 0 0", paddingInlineStart: 18 }}>
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
            <Link to="/rms/customers" style={{ display: "inline-block", marginTop: 8, fontWeight: 620 }}>
              {t("checkout.updateCustomer", "Update the {customer} record →", {
                customer: tLower("customer", "customer"),
              })}
            </Link>
          </div>
        </Banner>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 22, alignItems: "start" }}>
        <div>
          {/* --- Verify --- */}
          <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
            <h2 className="rms-section-title">1. Verify</h2>
            <SummaryRow label={t("customer", "Customer")} value={`${customer?.first_name} ${customer?.last_name || ""}`.trim()} />
            <SummaryRow
              label="ID"
              value={
                customer?.id_verification?.verified_at ? (
                  <StatusPill status="verified" label="verified" />
                ) : (
                  <StatusPill status="pending" label="not verified" />
                )
              }
            />
            <SummaryRow label={t("assetSingular", "Asset")} value={`${asset?.asset_code} — ${asset?.name}`} />
            <SummaryRow
              label={isContinuous ? t("checkout.moveIn", "Move-in") : t("checkout.period", "Period")}
              value={isContinuous ? formatDateTime(reservation.starts_at) : `${formatDateTime(reservation.starts_at)} → ${formatDateTime(reservation.ends_at)}`}
            />
            <SummaryRow label={t("checkoutFlow.quoted", "Quoted")} value={formatMoney(reservation.quoted_total_cents)} />
            {asset?.deposit_cents && workflow.depositRequired ? (
              <SummaryRow label={t("checkout.depositToHold", "{deposit} to hold", { deposit: t("deposit", "Deposit") })} value={formatMoney(asset.deposit_cents)} />
            ) : null}
          </section>

          {/* --- Certification (construction) --- */}
          {workflow.certificationRequired ? (
            <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
              <h2 className="rms-section-title">{t("checkoutFlow.operatorCertification", "Operator certification")}</h2>
              <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
                {asset?.custom_fields?.required_certification &&
                asset.custom_fields.required_certification !== "none" ? (
                  // One sentence, one key. It used to be three fragments with
                  // the certificate name bolded between them, and the two
                  // English halves either side could not follow the noun when
                  // a language moves it.
                  t(
                    "checkoutFlow.certRequired",
                    "This machine requires a valid {certificate} certificate. The gate above checks the account's certifications on file and blocks release if none is verified and unexpired.",
                    {
                      certificate: optionLabel(
                        optionsForField(config, "required_certification"),
                        asset.custom_fields.required_certification
                      ),
                    }
                  )
                ) : (
                  "This machine requires no operator certification."
                )}
              </p>
              <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/rms/certifications">
                {t("checkoutFlow.manageCertifications", "Manage certifications")}
              </Link>
            </section>
          ) : null}

          {/* --- Condition / meters --- */}
          {checkoutFields.length ? (
            <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
              <h2 className="rms-section-title">
                {t("checkoutFlow.conditionReadings", "Condition & readings at {step}", {
                  step: tLower("checkout", "checkout"),
                })}
              </h2>
              <p style={{ fontSize: 12.5, color: "var(--rms-muted)", marginTop: 0, marginBottom: 12 }}>
                {t("checkoutFlow.recordedNowSoThe", "Recorded now so the return can be compared against it.")}
              </p>
              <div className="rms-field-grid">
                {checkoutFields.map((f) => (
                  <DynamicField
                    key={f.key}
                    field={{ ...f, required: true }}
                    value={captured[f.key]}
                    allValues={captured}
                    onChange={(v) => setCaptured((c) => ({ ...c, [f.key]: v }))}
                  />
                ))}
              </div>
              {workflow.photosRequiredAtCheckout ? (
                <Banner tone="info">
                  {t("checkoutFlow.thisBusinessTypeExpects", "This business type expects condition photos at checkout. Photo capture is wired to the condition report record and will attach here once storage is connected.")}
                </Banner>
              ) : null}
            </section>
          ) : null}

          {/* --- The contract --- */}
          {/* Two shapes on purpose. With documents, the customer reads the
              operator's own text and the checkbox says so. Without them -- an
              owner who has never opened Settings > Agreements -- the original
              placeholder still appears, so a counter that worked yesterday
              still works today. */}
          {documents.length ? (
            <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
              <h2 className="rms-section-title">
                {t("checkout.documents", "What the {customer} is signing", {
                  customer: tLower("customer", "customer"),
                })}
              </h2>

              {blanksInDocuments.length ? (
                <Banner tone="warn">
                  {t(
                    "checkout.blanks",
                    "Nothing on file for: {list}. Those print as blank lines to fill in by hand. An owner can set them once in Settings.",
                    { list: blanksInDocuments.join(", ") }
                  )}
                </Banner>
              ) : null}

              {unknownInDocuments.length ? (
                <Banner tone="danger">
                  {t(
                    "checkout.unknownTokens",
                    "This agreement contains {list}, which this app does not recognise. It will print exactly as written — an owner should fix the wording in Settings.",
                    { list: unknownInDocuments.map((x) => `{{${x}}}`).join(", ") }
                  )}
                </Banner>
              ) : null}

              {documents.map((doc) => (
                <div key={doc.docKey} style={{ marginBottom: 14 }}>
                  <h3 style={{ fontSize: 13.5, margin: "0 0 6px" }}>{doc.title}</h3>
                  <AgreementReader body={doc.body} maxHeight={220} dense />
                </div>
              ))}

              <label className="rms-checkline">
                <input
                  type="checkbox"
                  checked={!!consentAt}
                  onChange={(e) => setConsentAt(e.target.checked ? new Date().toISOString() : null)}
                />
                {t(
                  "checkout.consent",
                  "The {customer} has read the above and agrees to sign it electronically",
                  { customer: tLower("customer", "customer") }
                )}
              </label>

              <Field label={t("checkout.signerName", "Name of the person signing")} required>
                <TextInput value={signerName} onChange={setSignerName} autoComplete="off" />
              </Field>
            </section>
          ) : workflow.waiverRequired ? (
            <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
              <h2 className="rms-section-title">{t("checkoutFlow.liabilityWaiver", "Liability waiver")}</h2>
              <div
                style={{
                  maxHeight: 130, overflowY: "auto", fontSize: 12.5, lineHeight: 1.55,
                  color: "var(--rms-muted-strong)", background: "var(--rms-subtle)",
                  border: "1px solid var(--rms-border)", borderRadius: 9, padding: 12, marginBottom: 12,
                }}
              >
                <p style={{ marginTop: 0 }}>
                  {t("checkoutFlow.theUndersignedAcknowledgesThe", "The undersigned acknowledges the inherent risks of operating the equipment described in this agreement, confirms they are legally permitted and physically fit to operate it, and agrees to return it in the condition recorded at checkout, reasonable wear excepted.")}
                </p>
                <p style={{ marginBottom: 0 }}>
                  {t("checkoutFlow.ownerCanReplaceThis", "Your own agreement goes here. An owner sets it up in Settings › Agreements — after that, this box shows your wording and the signed copy is kept with the booking.")}
                </p>
              </div>
              <label className="rms-checkline">
                <input type="checkbox" checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)} />
                {t("checkout.waiverAccepted", "The {customer} has read and accepted the waiver", {
                  customer: tLower("customer", "customer"),
                })}
              </label>
            </section>
          ) : null}

          {/* --- Signature --- */}
          <section className="rms-card rms-card-pad">
            <h2 className="rms-section-title">
              {t("checkoutFlow.signature", "Signature")}
              {workflow.signatureRequiredAtCheckout ? "" : ` ${t("common.optional", "(optional)")}`}
            </h2>
            <SignaturePad onChange={setSignature} />
            <Field label={t("checkoutFlow.notesForThisCheckout", "Notes for this checkout")}>
              <textarea className="rms-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth recording…" />
            </Field>
          </section>
        </div>

        <aside style={{ position: "sticky", top: 84 }}>
          <div className="rms-card rms-card-pad">
            <h3 className="rms-section-title">{t("checkoutFlow.readyToRelease", "Ready to release?")}</h3>
            <Checklist
              items={[
                { label: t("checkout.noBlockers", "No blockers"), done: blockers.length === 0 },
                ...(usingDocuments
                  ? [{ label: t("checkout.contractDone", "Agreement accepted and named"), done: contractAccepted }]
                  : workflow.waiverRequired
                    ? [{ label: t("checkout.waiverDone", "Waiver accepted"), done: waiverAccepted }]
                    : []),
                ...(checkoutFields.length ? [{ label: t("checkout.readingsDone", "Readings captured"), done: requiredFieldsPresent(checkoutFields, captured) }] : []),
                ...(workflow.signatureRequiredAtCheckout ? [{ label: t("checkout.signatureDone", "Signature captured"), done: !!signature }] : []),
              ]}
            />
            <button
              type="button"
              className="rms-btn rms-btn--primary"
              style={{ width: "100%", marginTop: 14 }}
              disabled={!canRelease || busy}
              onClick={handleRelease}
            >
              {busy ? t("checkout.releasing", "Releasing…") : isContinuous ? t("checkout.completeMoveIn", "Complete move-in") : t("checkout.release", "Release {item}", { item: tLower("assetSingular", "asset") })}
            </button>
            <p className="rms-help" style={{ marginTop: 8 }}>
              {t("checkout.releaseNote", "This opens an agreement and marks the {item} as {state}.", {
                item: tLower("assetSingular", "asset"),
                state: isContinuous
                  ? t("checkout.stateOccupied", "occupied")
                  : t("checkout.stateOut", "out"),
              })}
            </p>
          </div>
        </aside>
      </div>
    </>

      {signing ? (
        <PinModal
          title={t("pin.signRelease", "Sign this release")}
          explain={t("pin.signReleaseHelp", "Your PIN records who handed this over.")}
          onCancel={() => setSigning(false)}
          onResolved={release}
        />
      ) : null}
    </>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: "1px solid var(--rms-border)", fontSize: 13.5 }}>
      <span style={{ color: "var(--rms-muted)" }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: "end" }}>{value}</span>
    </div>
  );
}

function Checklist({ items }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
      {items.map((i) => (
        <li key={i.label} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", color: i.done ? "var(--rms-ok)" : "var(--rms-muted)" }}>
          <span aria-hidden="true">{i.done ? "✓" : "○"}</span>
          {i.label}
        </li>
      ))}
    </ul>
  );
}

function requiredFieldsPresent(fields, values) {
  return fields.every((f) => {
    const v = values[f.key];
    if (Array.isArray(v)) return true; // checklists may legitimately be empty
    return v !== undefined && v !== null && v !== "";
  });
}

export default CheckoutFlow;
